import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, ShoppingCart, Heart, Activity, Droplets, Droplet, Shield, Syringe, Package2, TrendingUp } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatINR } from '@/data/mockData';

const CAT_ICON_MAP = {
  'Fertility':          { Icon: Heart,      color: '#E91E63', bg: '#FFF0F3' },
  "Women's Wellness":   { Icon: Activity,   color: '#9C27B0', bg: '#F3E5F5' },
  'Hormonal Support':   { Icon: Droplets,   color: '#3F51B5', bg: '#E8EAF6' },
  'Gynae Hematinics':   { Icon: Droplet,    color: '#F44336', bg: '#FFEBEE' },
  'Bone & Supportive':  { Icon: Shield,     color: '#388E3C', bg: '#E8F5E9' },
  'Injectable Range':   { Icon: Syringe,    color: '#FF5722', bg: '#FBE9E7' },
  'Miscellaneous':      { Icon: Package2,   color: '#607D8B', bg: '#ECEFF1' },
  'Top Movers':         { Icon: TrendingUp, color: '#FF9800', bg: '#FFF3E0' },
};

export default function ProductRequestScreen() {
  const { goBack, navigate, screenParams } = useApp();
  const { product } = screenParams || {};

  const [selectedOffer, setSelectedOffer] = useState(0);
  const [qty, setQty] = useState(null); // set after offer selection

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <div className="font-semibold">No product selected</div>
        <button onClick={goBack} className="mt-4 text-[#FF5722] font-bold">Go Back</button>
      </div>
    );
  }

  const info = CAT_ICON_MAP[product.category] || { Icon: Package2, color: '#607D8B', bg: '#ECEFF1' };
  const { Icon } = info;
  const isVial = product.pack.toLowerCase().includes('vial');
  const pts = Math.round(product.mrp * 0.82);
  const baseDiscount = 18;
  const unit = isVial ? 'Vials' : 'Boxes';

  const offerTiers = isVial
    ? [
        { qty: 10,  discount: 2,  label: `Buy 10 ${unit}` },
        { qty: 25,  discount: 5,  label: `Buy 25 ${unit}` },
        { qty: 50,  discount: 10, label: `Buy 50 ${unit}` },
      ]
    : [
        { qty: 100, discount: 2,  label: `Buy 100 ${unit}` },
        { qty: 200, discount: 5,  label: `Buy 200 ${unit}` },
        { qty: 300, discount: 10, label: `Buy 300 ${unit}` },
      ];

  const activeOffer = selectedOffer !== null ? offerTiers[selectedOffer] : null;
  const displayQty = qty !== null ? qty : (activeOffer ? activeOffer.qty : offerTiers[0].qty);
  const step = isVial ? 1 : 10;

  const handleSelectOffer = (idx) => {
    setSelectedOffer(idx);
    setQty(offerTiers[idx].qty);
  };

  const handleRemoveOffer = () => {
    setSelectedOffer(null);
    setQty(step);
  };

  return (
    <div className="bg-white pb-6">
      {/* Back button */}
      <div className="px-4 pt-5 pb-1">
        <button
          data-testid="product-request-back"
          onClick={goBack}
          className="tap-scale"
        >
          <ChevronLeft size={26} color="#FF5722" strokeWidth={2.5} />
        </button>
      </div>

      {/* Product image area */}
      <div
        className="mx-4 rounded-2xl flex items-center justify-center"
        style={{ height: '180px', background: info.bg }}
      >
        <Icon size={90} color={info.color} strokeWidth={1} />
      </div>

      {/* Carousel dots */}
      <div className="flex justify-center gap-1.5 mt-3 mb-4">
        <div className="h-1.5 w-5 rounded-full bg-[#FF5722]" />
        <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      </div>

      <div className="px-4">
        {/* Product title */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="font-black text-xl text-gray-900 tracking-tight">
            {product.name.toUpperCase()}
          </span>
          <span className="text-sm text-gray-400 font-medium">{product.pack}</span>
        </div>

        {/* Composition */}
        <div className="bg-gray-50 rounded-xl p-3.5 mb-4 border border-gray-100">
          <div className="font-bold text-sm text-gray-800 mb-1">Composition:</div>
          <div className="text-sm text-gray-600 leading-snug">{product.description}</div>
        </div>

        {/* M.R.P / PTS / Discount */}
        <div className="bg-white rounded-xl p-4 mb-5 border border-gray-200">
          <div className="grid grid-cols-3">
            <div>
              <div className="text-xs text-gray-400 font-semibold mb-1">M.R.P</div>
              <div className="font-black text-lg text-gray-900">₹{formatINR(product.mrp)}</div>
            </div>
            <div className="border-l border-r border-gray-100 px-3">
              <div className="text-xs text-gray-400 font-semibold mb-1">PTS</div>
              <div className="font-black text-lg text-gray-900">₹{formatINR(pts)}</div>
            </div>
            <div className="pl-3">
              <div className="text-xs text-gray-400 font-semibold mb-1">Discount</div>
              <div className="font-black text-lg text-green-500">{baseDiscount}%</div>
            </div>
          </div>
        </div>

        {/* Offer Selection */}
        <div className="font-bold text-base text-gray-900 mb-3">Offer Selection</div>
        <div className="flex flex-col gap-2.5 mb-5">
          {offerTiers.map((tier, idx) => {
            const isSelected = selectedOffer === idx;
            return (
              <div
                key={idx}
                className="flex items-center justify-between p-3.5 rounded-xl"
                style={
                  isSelected
                    ? { background: '#F5F5F5', border: '1px solid #E0E0E0' }
                    : { background: 'white', border: '1.5px dashed #FF5722' }
                }
              >
                <div>
                  <div className="font-bold text-sm text-gray-800">{tier.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Unlock +{tier.discount}% Additional Discount
                  </div>
                </div>
                {isSelected ? (
                  <button
                    data-testid={`offer-remove-${idx}`}
                    onClick={handleRemoveOffer}
                    className="border border-gray-300 bg-white text-gray-600 text-xs font-bold px-3 py-1.5 rounded-lg tap-scale"
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    data-testid={`offer-select-${idx}`}
                    onClick={() => handleSelectOffer(idx)}
                    className="bg-[#FF5722] text-white text-xs font-bold px-4 py-1.5 rounded-lg tap-scale"
                  >
                    Select
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Quantity counter */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-2 mb-3">
          <button
            data-testid="qty-decrease"
            onClick={() => setQty(Math.max(step, displayQty - step))}
            className="w-11 h-11 bg-gray-200 rounded-lg flex items-center justify-center font-black text-xl text-gray-600 tap-scale"
          >
            −
          </button>
          <span
            data-testid="qty-display"
            className="font-black text-2xl text-gray-900"
          >
            {displayQty}
          </span>
          <button
            data-testid="qty-increase"
            onClick={() => setQty(displayQty + step)}
            className="w-11 h-11 bg-gray-900 rounded-lg flex items-center justify-center tap-scale"
          >
            <Plus size={20} color="white" strokeWidth={2.5} />
          </button>
        </div>

        {/* Discount banner */}
        {activeOffer && (
          <div
            className="rounded-xl px-4 py-2.5 text-center mb-5"
            style={{ background: '#FFF9C4', border: '1px solid #F9E04B' }}
          >
            <span className="text-yellow-800 font-bold text-sm">
              +{activeOffer.discount}% Additional discount applied
            </span>
          </div>
        )}

        {/* View Cart */}
        <div className="flex justify-center mb-6">
          <button
            data-testid="view-cart-btn"
            onClick={() => navigate('CHECKOUT', { product, qty: displayQty })}
            className="flex items-center gap-3 text-white font-bold px-7 py-3.5 rounded-full tap-scale"
            style={{ background: '#2563EB' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#FBBF24' }}
            >
              <ShoppingCart size={17} color="#1E40AF" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-sm font-black">View Cart</div>
              <div className="text-xs text-blue-200">10 Items</div>
            </div>
            <ChevronRight size={18} color="white" />
          </button>
        </div>

        {/* Send Purchase Request */}
        <button
          data-testid="send-purchase-request-btn"
          onClick={() => navigate('CHECKOUT', { product, qty: displayQty })}
          className="w-full text-white font-black text-base py-4 rounded-full tap-scale"
          style={{ background: 'linear-gradient(to right, #FF7043, #FF5722)' }}
        >
          Send Purchase Request
        </button>
      </div>
    </div>
  );
}
